import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import usePermissions from '@/hooks/usePermissions';
import { type ProductLine, useGetProductLinesQuery, useGetProductLineUnmatchedQuery } from '@/services/productLines';
import {
  type DataTableColumn,
  PalmettoButton,
  PalmettoDataTable,
  PalmettoSelect,
  PalmettoTag,
} from '@palmetto/dsm-components';
import { useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { EquipmentRecordStatus, ProductLineUnmatchedProduct, ProgramType, UserPermissions } from 'types';

import {
  enumParam,
  EquipmentRecordStatusLabels,
  EquipmentRecordStatusTagColors,
  statusOptions,
  statusValues,
} from '../equipment-manufacturers/equipmentLabels';

import { ProductLineBreadcrumb, productLinesPath, productLinesTitle } from './ProductLineBreadcrumb';
import { noProductLineLabel, ProductLineValueTags } from './ProductLineValueTags';

const getRowId = (row: ProductLine) => row.id;
const getUnmatchedRowId = (row: ProductLineUnmatchedProduct) => row.productId;

const unmatchedColumns: DataTableColumn<ProductLineUnmatchedProduct>[] = [
  { id: 'manufacturer', header: 'Manufacturer', width: '200px', render: (row) => row.manufacturer },
  {
    id: 'name',
    header: 'Battery model',
    cellType: 'two-line',
    render: (row) => (row.sku ? `${row.name}\n${row.sku}` : row.name),
  },
  {
    id: 'productLineValue',
    header: 'Catalog product line',
    width: '240px',
    render: (row) => row.productLineValue ?? noProductLineLabel,
  },
];

export const ProductLineList = () => {
  const navigate = useNavigate();
  const canEditAvl = usePermissions().includes(UserPermissions.canImportAvlData);
  const [searchParams, setSearchParams] = useSearchParams();

  const status =
    enumParam<EquipmentRecordStatus>(searchParams.get('status'), statusValues) || EquipmentRecordStatus.active;

  const { data, isLoading, isFetching, error, refetch } = useGetProductLinesQuery({
    status,
    programType: ProgramType.energyBackup,
  });
  const {
    data: unmatched,
    isLoading: isUnmatchedLoading,
    isFetching: isUnmatchedFetching,
    error: unmatchedError,
    refetch: refetchUnmatched,
  } = useGetProductLineUnmatchedQuery(ProgramType.energyBackup);

  const items = data?.data ?? [];
  const unmatchedProducts = unmatched?.unmatchedProducts ?? [];

  const orphanValuesByLine = useMemo(() => {
    const byLine = new Map<string, Set<string>>();
    for (const { productLineId, value } of unmatched?.orphanValues ?? []) {
      const values = byLine.get(productLineId) ?? new Set<string>();
      values.add(value);
      byLine.set(productLineId, values);
    }
    return byLine;
  }, [unmatched]);

  const handleStatusChange = useCallback(
    (details: { value: string[] }) => {
      const next = new URLSearchParams(searchParams);
      const value = details.value[0] ?? '';
      if (value) next.set('status', value);
      else next.delete('status');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleNewProductLine = useCallback(() => navigate(`${productLinesPath}/new`), [navigate]);

  const columns: DataTableColumn<ProductLine>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        width: '240px',
        cellType: 'link',
        render: (row) => <Link to={`${productLinesPath}/${row.id}`}>{row.name}</Link>,
      },
      {
        id: 'values',
        header: 'Product Catalog values',
        cellType: 'tag',
        render: (row) => <ProductLineValueTags values={row.values} orphanValues={orphanValuesByLine.get(row.id)} />,
      },
      {
        id: 'status',
        header: 'Status',
        width: '120px',
        cellType: 'tag',
        render: (row) => (
          <PalmettoTag
            label={EquipmentRecordStatusLabels[row.status] ?? row.status}
            color={EquipmentRecordStatusTagColors[row.status]}
          />
        ),
      },
    ],
    [orphanValuesByLine],
  );

  return (
    <DsmPageContainer title={productLinesTitle}>
      <PageHeader title={productLinesTitle} eyebrow={<ProductLineBreadcrumb />}>
        {canEditAvl && (
          <PalmettoButton emphasis="primary" onClick={handleNewProductLine}>
            + New Product Line
          </PalmettoButton>
        )}
      </PageHeader>

      <div className="mx-6 mb-4 grid grid-cols-3 gap-4">
        <PalmettoSelect
          label="Status"
          items={statusOptions.map((opt) => ({ ...opt, selected: opt.value === status }))}
          onValueChange={handleStatusChange}
        />
      </div>

      <div className="mx-6 mb-16 overflow-x-auto">
        <PalmettoDataTable
          columns={columns}
          data={items}
          getRowId={getRowId}
          isLoading={isLoading || isFetching}
          error={error}
          onRetry={refetch}
          ariaLabel="Product lines"
        />
      </div>

      <section className="mx-6 mb-16 flex flex-col gap-8" aria-labelledby="unmatched-battery-models">
        <h2 id="unmatched-battery-models" className="text-left heading-200 text-text-primary">
          Unmatched battery models
        </h2>
        <p className="body-200 text-text-secondary">
          These Energy Backup batteries resolve to no active Product Line, because their catalog product line matches
          none of the values above or they have none. Nothing is created for them automatically; add their product
          line value to a line to cover them.
        </p>
        {!unmatchedError && !isUnmatchedLoading && !isUnmatchedFetching && unmatchedProducts.length === 0 ? (
          <p className="body-200 text-text-primary">Every battery model resolves to a Product Line.</p>
        ) : (
          <div className="overflow-x-auto">
            <PalmettoDataTable
              columns={unmatchedColumns}
              data={unmatchedProducts}
              getRowId={getUnmatchedRowId}
              isLoading={isUnmatchedLoading || isUnmatchedFetching}
              error={unmatchedError}
              onRetry={refetchUnmatched}
              ariaLabel="Unmatched battery models"
            />
          </div>
        )}
      </section>
    </DsmPageContainer>
  );
};
